import React from "react";
import { Shield, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy({ onBack }) {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#E52020] font-['Manrope'] mb-8 hover:underline">
          <ArrowLeft className="w-4 h-4" />BACK TO APP
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-8 h-8 text-[#E52020]" />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] font-['Manrope'] tracking-wide">Privacy Policy</h1>
        </div>
        <p className="text-xs text-[#999999] font-mono mb-8">Last updated: April 2026</p>

        <div className="space-y-8 text-[#475569] text-sm leading-relaxed">
          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">OVERVIEW</h2>
            <p>DRIVE$PEND is a New Zealand fuel cost calculator. We respect your privacy and are committed to being transparent about the data we collect. This policy explains what information we gather and how we use it, in accordance with the New Zealand Privacy Act 2020.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">WHAT WE COLLECT</h2>
            <h3 className="font-semibold text-[#1A1A1A] mt-3 mb-1">Account Information (if you register)</h3>
            <p>If you create an account, we store your name, email address, and a securely hashed password. This is used solely for authentication and is never shared with third parties.</p>

            <h3 className="font-semibold text-[#1A1A1A] mt-4 mb-1">Anonymous Trip Data</h3>
            <p>When you save a trip to history, we collect anonymous usage data to power community statistics. This data includes:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li>Trip distance (km)</li>
              <li>Fuel type and price per litre</li>
              <li>Fuel consumption rate (L/100km)</li>
              <li>Cost breakdown (fuel, RUC, tolls, ferry)</li>
              <li>City/town names for origin and destination (not exact addresses or GPS coordinates)</li>
              <li>Region and vehicle type (if provided)</li>
              <li>Whether the trip was a return trip</li>
            </ul>
            <p className="mt-3 font-semibold text-[#E52020]">This trip data is fully anonymous. We do not store your user ID, IP address, device information, or any other identifier alongside it. There is no way to link this data back to you.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">WHAT WE DON'T COLLECT</h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Exact GPS coordinates or street addresses in anonymous data</li>
              <li>IP addresses</li>
              <li>Device fingerprints or identifiers</li>
              <li>Browsing history or tracking cookies</li>
              <li>Payment or financial information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">HOW WE USE THE DATA</h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Community Statistics:</strong> Anonymous trip data is aggregated to show users general trends like average trip costs, popular routes, and fuel type preferences.</li>
              <li><strong>Service Improvement:</strong> Understanding how the calculator is used helps us improve the app.</li>
              <li><strong>Authentication:</strong> Your email and password are used solely to manage your account and sync your personal trip history across devices.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">DATA STORAGE & SECURITY</h2>
            <p>Data is stored in a secure database. Passwords are hashed using bcrypt and are never stored in plain text. Anonymous trip data is stored separately from user accounts with no linking identifiers.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">THIRD-PARTY SERVICES</h2>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Gaspy NZ:</strong> We fetch live fuel price data from Gaspy. No user data is sent to Gaspy.</li>
              <li><strong>Mapbox:</strong> Map tiles and routing are provided by Mapbox. Mapbox may collect anonymous usage metrics as per their own privacy policy.</li>
              <li><strong>Resend:</strong> Password reset emails are sent via Resend. Only your email address is shared with Resend for this purpose.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">YOUR RIGHTS</h2>
            <p>Under the NZ Privacy Act 2020, you have the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li>Access any personal information we hold about you</li>
              <li>Request correction of your personal information</li>
              <li>Request deletion of your account and associated data</li>
            </ul>
            <p className="mt-2">Anonymous trip data cannot be linked to individual users and therefore cannot be retrieved or deleted on a per-user basis.</p>
          </section>

          <section>
            <h2 className="text-base font-bold text-[#1A1A1A] font-['Manrope'] tracking-wider mb-2">CONTACT</h2>
            <p>If you have any questions about this privacy policy or wish to exercise your rights, please contact us at <a href="mailto:privacy@drivespend.nz" className="text-[#E52020] hover:underline">privacy@drivespend.nz</a>.</p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-[#EBE6DC] text-center">
          <button onClick={onBack} className="text-sm text-[#E52020] font-['Manrope'] hover:underline">Back to DRIVE$PEND</button>
        </div>
      </div>
    </div>
  );
}
